import { config } from "../config";
import { report } from "./data.service";
import { deviceInfo, capitalizeFirstLetter } from "../lib/util";
import { Router, Settings as LightningSettings } from "@lightningjs/sdk";
import { Store } from "./app.store.service";

export const ViewNames = {
  Radiostations: "radiostations",
  SplashScreen: "splashscreen",
  Store: "store",
  Exit: "exit",
};

export const TrackingAction = {
  ChangeView: "viewChange",
  ChannelChange: "channelChange",
  StartApp: "radioStart",
  ExitApp: "radioExit",
  PlayerStart: "playerStart",
  Error: "appError",
  FetchRadiostationsFailure: "failedToFetchRadiostations",
  FetchConfigRadiostationsFailure: "failedToFetchConfiguration",
  NowNextFailure: "nowNextFailure",
};

const TrackingMsg = {
  ChangeView: "View changed",
  StartApp: "radioStart",
  ExitApp: "radioExit",
  PlayerStart: "Player start",
  Error: "App error",
  FetchRadiostationsFailure: "Failed to fetch radiostations",
  FetchConfigRadiostationsFailure: "Failed to fetch configuration",
  NowNextFailure: "Failed to fetch nowNext",
  ChannelChange: "Channel Change",
};

export const TrackingCategory = {
  UserAction: "user_action",
  PageView: "page_view",
  AppError: "app_error",
  UserError: "user_error",
};

let reportObjectList = [];
let sessionStartTime = new Date().getTime();
export class ReportingService {
  getCurrentTrackingList() {
    return reportObjectList;
  }

  static async clearTrackingData() {
    reportObjectList = [];
  }

  static async trackViewChange(from, to, isMandatory = false) {
    const trackViewObject = {
      action: TrackingAction.ChangeView,
      customData: {
        viewName: to,
        source: from,
      },
    };
    if (isMandatory) {
      this.trackAndReport(trackViewObject);
    } else {
      this.silentTrack(trackViewObject);
    }
  }

  static async trackStart({ isForced } = { isForced: false }) {
    const accountType = Store.accountType;
    if (!this.startTracked || isForced) {
      let reportObject = {
        action: TrackingAction.StartApp,
        customData: {
          source: "store",
          url: "/",
          accountType: accountType,
        },
        customAssetId: "0",
        sendImmediate: true,
      };
      this.trackAndReport(reportObject);

      if (!isForced) {
        this.startTracked = true;
      }
    }
  }

  static async trackExit() {
    const accountType = Store.accountType;

    let reportObject = {
      action: TrackingAction.ExitApp,
      sendImmediate: true,
      customData: {
        url: Router.getActiveRoute(),
        accountType: accountType,
      },
      customAssetId: "0",
    };
    this.trackAndReport(reportObject);
  }

  static async getReportAndSend(options) {
    const trackingCategory = await this.getTrackingCategory(options.action);
    const asset = await this.getAsset(options.action, options.assetId);
    const code = await this.getCode(options.action);
    const trackingMsg = await this.getTrackingMsg(options.action, options.errorMsg || "");
    const customObject = await this.getCustomData(options.action, options.customData || {});
    const partitionDate = await this.getPartitionDate();
    const sendImmediate = options.sendImmediate || false;
    const forceIgnore = options.forceIgnore || false;

    let reportObject = new Report({
      message: trackingMsg,
      category: trackingCategory,
      custom: customObject,
      code: code,
      asset: asset,
      partitionDate: partitionDate,
      deviceInfo: deviceInfo,
      correlationId: "",
    });

    if (reportObject.isValidReport()) {
      reportObjectList.push(reportObject);
    }
    // Send report is reporting queue is full
    if (sendImmediate) {
      if (forceIgnore) return;
      this.sendReport();
    }
  }

  static async silentTrack(
    options = {
      action: TrackingAction.StartApp,
      customData: {},
      errorMsg: "",
      assetId: "",
      sendImmediate: false,
      forceIgnore: false,
    }
  ) {
    this.getReportAndSend(options);
  }

  static async getTrackingMsg(trackingAction, errorMsg) {
    const defaultErrorMsg = "Unknown error";

    let trackingActionMsgMapping = new Map([
      [TrackingAction.StartApp, TrackingMsg.StartApp],
      [TrackingAction.ChannelChange, TrackingMsg.ChannelChange],
      [TrackingAction.Error, TrackingMsg.Error],
      [TrackingAction.ExitApp, TrackingMsg.ExitApp],
      [TrackingAction.ChangeView, TrackingMsg.ChangeView],
      [TrackingAction.PlayerStart, TrackingMsg.PlayerStart],
      [TrackingAction.FetchRadiostationsFailure, TrackingMsg.FetchRadiostationsFailure],
      [TrackingAction.FetchConfigFailure, TrackingMsg.FetchConfigFailure],
      [TrackingAction.NowNextFailure, TrackingMsg.NowNextFailure],
    ]);

    let trackingMsg = "";

    if (trackingAction == TrackingAction.Error) {
      trackingMsg = errorMsg;
    } else {
      const trackingMsgMapped = trackingActionMsgMapping.get(trackingAction);
      trackingMsg = trackingMsgMapped ? trackingMsgMapped : "";
    }

    return trackingMsg.length > 0 ? trackingMsg : defaultErrorMsg;
  }

  static async getTrackingCategory(trackingAction) {
    switch (trackingAction) {
      case TrackingAction.ChangeView:
        return TrackingCategory.PageView;
      case TrackingAction.Error:
      case TrackingAction.FetchRadiostationsFailure:
      case TrackingAction.NowNextFailure:
        return TrackingCategory.AppError;
      default:
        return TrackingCategory.UserAction;
    }
  }

  static async sendReport() {
    return new Promise(resolve => {
      let silentReportsLimit = `${config.appConfig.silentReportsLimit}`;

      if (silentReportsLimit != undefined && silentReportsLimit > 0) {
        const reportPromises = [];

        // Limit number of reports per request if set in config
        if (reportObjectList.length > 0) {
          for (let i = 0; i < reportObjectList.length; i += silentReportsLimit) {
            const slicedReports = reportObjectList.slice(i, i + silentReportsLimit);
            reportPromises.push(report(slicedReports));
          }
        } else {
          return resolve();
        }

        const reportArrayImmutable = copyArray(reportObjectList);
        this.clearTrackingData();

        Promise.all(reportPromises)
          .then(() => {
            resolve();
          })
          .catch(() => {
            reportObjectList = reportArrayImmutable.concat(reportObjectList);
            this.createErrorReport();
            resolve();
          });
      } else {
        // No limit set or limit is 0. Send all tracked events at once.
        if (reportObjectList.length > 0) {
          const reportArrayImmutable = copyArray(reportObjectList);
          this.clearTrackingData();

          report(reportArrayImmutable)
            .then(() => {
              // Clear stored reports when reports were sent successfully
              resolve();
            })
            .catch(error => {
              reportObjectList = reportArrayImmutable.concat(reportObjectList);
              this.createErrorReport();
              resolve();
            });
        } else {
          resolve();
        }
      }
    });
  }

  static async createErrorReport() {
    this.silentTrack({
      action: TrackingAction.Error,
      errorMsg: "requestError",
      sendImmediate: true,
      forceIgnore: true,
      customData: {
        url: config.appConfig.reportingUrl,
        errorCode: 400,
      },
    });
  }

  static async trackAndReport(options) {
    const trackingAction = options.action;
    const customData = options.customData || {};
    const errorMsg = options.errorMsg || "";
    const assetId = "";

    this.silentTrack({
      action: trackingAction,
      customData: customData,
      errorMsg: errorMsg,
      sendImmediate: options.sendImmediate || false,
      assetId: assetId,
    });
  }

  static async getCustomData(action, custom) {
    let customObject = custom;
    const accountType = Store.accountType;
    if (action == TrackingAction.ExitApp) {
      customObject = Object.assign(custom, {
        sessionTime: new Date().getTime() - sessionStartTime,
        source: "menu",
        accountType: accountType,
      });
    }

    return customObject;
  }

  static async getPartitionDate() {
    let today = new Date();
    let month = "" + (today.getMonth() + 1);
    let year = today.getFullYear();

    if (month.length < 2) {
      month = "0" + month;
    }

    return [year, month].join("-");
  }

  static async getCode(trackingAction) {
    if (trackingAction == TrackingAction.StartApp || trackingAction == TrackingAction.ExitApp) {
      return 101;
    } else {
      return 200;
    }
  }

  static async getAsset(trackingAction, assetId) {
    if (trackingAction === TrackingAction.Error) return undefined;
    if (trackingAction === TrackingAction.ExitApp) return undefined;

    const type = trackingAction;
    const asset = {
      id: assetId ? assetId : "0",
      type: type,
    };

    return asset;
  }
}

function copyArray(src) {
  return [...src];
}

export class Report {
  constructor(options) {
    this.timestamp = new Date().getTime();
    this.id = `${this.timestamp}`;

    if (options.deviceInfo && options.deviceInfo.metadata.deviceId) {
      this.id = `${this.timestamp}-${options.deviceInfo.metadata.deviceId}`;
    }
    const client = {
      version: LightningSettings.get("app", "version"),
      ownerKey: config.appConfig.ownerKey,
      deviceId: options.deviceInfo.metadata.deviceId,
      appPlatform: options.deviceInfo.platform,
    };

    this.clientVersion = client.version || "unknown";
    this.clientOwnerKey = client.ownerKey || "unknown";
    this.clientDeviceId = client.deviceId || "unknown";
    this.clientPlatform = client.appPlatform || "unknown";

    this.category = options.category;
    this.partitionDate = options.partitionDate;
    this.message = options.message;

    this.correlationId = options.correlationId;

    const customData = options.custom;
    if (customData) {
      if (customData.source) this.customSource = customData.source;
      if (customData.destination) this.destination = customData.destination;
      if (customData.accountType) this.customAccountType = customData.accountType;
      if (customData.viewName) this.customViewName = customData.viewName;
      if (customData.sessionTime) this.customSessionTime = customData.sessionTime;
      if (customData.accountId) this.customAccountId = customData.accountId;
      if (customData.deviceCode) this.customDeviceCode = customData.deviceCode;
      if (customData.userCode) this.customUserCode = customData.userCode;
      if (customData.statusMessage) this.customStatusMessage = customData.statusMessage;
      if (customData.errorMessage) this.customErrorMessage = customData.errorMessage;
      if (customData.errorCode) this.customErrorCode = customData.errorCode;
      if (customData.filterIds) this.customFilterIds = customData.filterIds;
      if (customData.videoIndex !== undefined) this.customVideoIndex = customData.videoIndex;
      if (customData.channelId) this.customChannelId = customData.channelId;
      if (customData.channelName) this.customChannelName = customData.channelName;
      if (customData.categoryName) this.customCategoryName = customData.categoryName;
      if (customData.errorMessage) this.customErrorMessage = customData.errorMessage;
      if (customData.errorCode) this.customErrorCode = customData.errorCode;
      if (customData.playerType)
        this.customPlayerType = capitalizeFirstLetter(customData.playerType);
    }

    const asset = options.asset;
    if (asset) {
      if (asset.id) this.customAssetId = asset.id;
    }

    const deviceInfo = options.deviceInfo;
    if (deviceInfo) {
      if (deviceInfo.metadata.stbType !== undefined)
        this.clientDeviceModel = deviceInfo.metadata.stbType || "unknown";
      if (deviceInfo.deviceSoftwareVersion !== undefined)
        this.clientDeviceSoftwareVersion = deviceInfo.deviceSoftwareVersion || "unknown";
      if (deviceInfo.metadata.householdId !== undefined)
        this.clientHouseholdId = deviceInfo.metadata.householdId || "unknown";
      if (deviceInfo.deviceClientId != undefined)
        this.clientId = deviceInfo.deviceClientId || "unknown";
    }
  }

  isValidReport() {
    return this.category && this.message && this.message.length > 0;
  }
}
